import { sum } from "../../helpers/array"
import { uniqueId } from "../../helpers/uniqueId"
import { CrewMember, Timesheet, Visit } from "./editVisitApi"
import { DateTime, Duration } from 'luxon'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export type BulkLaborParameters = {
  startAt: string
  endAt?: string
  workareaId?: number
  lunch?: number
  selectedCrewMemberIds: number[]
}

export type LaborChanges = {
  changedLabor: Timesheet[]
  addedLabor: Timesheet[]
  removedLaborIds: string[]
  bulkLabor: BulkLaborParameters
}

export const newLaborChanges = (startAt: string, endAt?: string): LaborChanges => ({
  changedLabor: [],
  addedLabor: [],
  removedLaborIds: [],
  bulkLabor: {
    startAt,
    endAt,
    selectedCrewMemberIds: []
  }
})

const updateLabor = (array: Timesheet[], id: string, onMatch: (x: Timesheet) => Timesheet) =>
  array.map(x => x.id === id ? onMatch(x) : x)

const updateOrAdd = (state: LaborChanges, timesheet: Timesheet, onMatch: (x: Timesheet) => Timesheet, onNewUpdate: () => Timesheet): LaborChanges => {
  if ([...state.changedLabor, ...state.addedLabor].some(x => x.id === timesheet.id)) {
    return {
      ...state,
      changedLabor: updateLabor(state.changedLabor, timesheet.id, onMatch),
      addedLabor: updateLabor(state.addedLabor, timesheet.id, onMatch)
    }
  }

  return {
    ...state,
    changedLabor: [...state.changedLabor, onNewUpdate()]
  }
}

export const updateWorkarea = (state: LaborChanges, timesheet: Timesheet, workareaId?: number): LaborChanges =>
  updateOrAdd(state, timesheet, x => ({ ...x, workareaId }), () => ({ ...timesheet, workareaId }))

export const updateStartedAt = (state: LaborChanges, timesheet: Timesheet, startedAt: string): LaborChanges =>
  updateOrAdd(state, timesheet, x => ({ ...x, startedAt }), () => ({ ...timesheet, startedAt }))

export const updateEndedAt = (state: LaborChanges, timesheet: Timesheet, endedAt?: string): LaborChanges =>
  updateOrAdd(state, timesheet, x => ({ ...x, endedAt }), () => ({ ...timesheet, endedAt }))

export const updateLunch = (state: LaborChanges, timesheet: Timesheet, lunch?: number) =>
  updateOrAdd(state, timesheet, x => ({ ...x, lunch }), () => ({ ...timesheet, lunch }))

export const newTimesheetForCrewMember = (state: LaborChanges, timesheet: Timesheet): LaborChanges => {
  return {
    ...state,
    addedLabor: [...state.addedLabor, {
      id: uniqueId(),
      crewMemberId: timesheet.crewMemberId,
      startedAt: timesheet.endedAt || timesheet.startedAt,
      gpsLog: [],
      canBeEdited: true
    }]
  }
}

export const removeTimesheet = (state: LaborChanges, timesheet: Timesheet): LaborChanges => {
  if (state.addedLabor.some(x => x.id === timesheet.id)) {
    return { ...state, addedLabor: [...state.addedLabor.filter(x => x.id !== timesheet.id)] }
  }

  return { ...state, removedLaborIds: [...state.removedLaborIds, timesheet.id] }
}

export const restoreTimesheet = (state: LaborChanges, timesheet: Timesheet): LaborChanges => {
  return {
    ...state,
    removedLaborIds: state.removedLaborIds.filter(x => x !== timesheet.id)
  }
}

const updateBulk = (state: LaborChanges, updater: (params: BulkLaborParameters) => BulkLaborParameters): LaborChanges => ({
  ...state,
  bulkLabor: updater(state.bulkLabor)
})

export const setBulkStartAt = (state: LaborChanges, startAt: string): LaborChanges => updateBulk(state, x => ({ ...x, startAt }))

export const setBulkEndAt = (state: LaborChanges, endAt?: string): LaborChanges => updateBulk(state, x => ({ ...x, endAt }))

const updateDatePart = (laborDate: string, visitDate: string): string => {
  const prior = DateTime.fromISO(laborDate)
  return DateTime.fromISO(visitDate).set({ hour: prior.hour, minute: prior.minute, second: 0 }).toUTC().toISO()
}

export const updateBulkDate = (state: LaborChanges, visitDate: string): LaborChanges => {
  const startAt = updateDatePart(state.bulkLabor.startAt, visitDate)
  const endAt = state.bulkLabor.endAt ? updateDatePart(state.bulkLabor.endAt, visitDate) : undefined

  return updateBulk(state, x => ({ ...x, startAt, endAt }))
}

export const setBulkWorkareaId = (state: LaborChanges, workareaId?: number): LaborChanges => updateBulk(state, x => ({ ...x, workareaId }))

export const setBulkLunch = (state: LaborChanges, lunch?: number): LaborChanges => updateBulk(state, x => ({ ...x, lunch }))

export const setSelectedCrewMembers = (state: LaborChanges, crewMemberIds: number[]): LaborChanges => updateBulk(state, x => ({
  ...x, selectedCrewMemberIds: crewMemberIds
}))

export const addCrewMembers = (state: LaborChanges): LaborChanges => {
  return {
    ...state,
    addedLabor: [
      ...state.addedLabor,
      ...state.bulkLabor.selectedCrewMemberIds.map(crewMemberId => ({
        id: uniqueId(),
        crewMemberId,
        startedAt: state.bulkLabor.startAt,
        endedAt: state.bulkLabor.endAt,
        workareaId: state.bulkLabor.workareaId,
        gpsLog: [],
        canBeEdited: true
      }))
    ],
    bulkLabor: {
      ...state.bulkLabor,
      selectedCrewMemberIds: []
    }
  }
}

export type EnrichedTimesheet = Timesheet & {
  totalTime: string
  totalMinutes: number
  crewMemberName: string
  crewMemberAvatar: string
  crewMemberInitials: string
  isRemoved: boolean
  workareaIdChanged: boolean
  startedAtChanged: boolean
  endedAtChanged: boolean
  lunchChanged: boolean
  hasTimeRangeError: boolean
  hasOverlapError: boolean
  cannotBeSubmitted: boolean
  startedAtDateMismatch: boolean
  endedAtDateMismatch: boolean
}

export const minutesToDisplayTime = (totalMinutes: number): string => {
  const totalTimeDiff = Duration.fromObject({ minutes: totalMinutes ? totalMinutes : 0 }).shiftTo('days', 'hours', 'minutes')
  const formattedParts = []

  if (totalTimeDiff.days !== 0) {
    formattedParts.push(`${totalTimeDiff.days} day${totalTimeDiff.days !== 1 ? 's' : ''}`)
  }

  if (totalTimeDiff.hours !== 0) {
    formattedParts.push(`${totalTimeDiff.hours} hr`)
  }

  if (totalTimeDiff.minutes !== 0) {
    formattedParts.push(`${totalTimeDiff.minutes} min`)
  }

  if (formattedParts.length === 0) {
    return "0 min"
  }

  return formattedParts.join(', ')
}

export const calculateTotalTime = (timesheet: Timesheet): { totalTime: string, totalMinutes: number } => {
  if (!timesheet.endedAt) {
    return { totalTime: '', totalMinutes: 0 }
  }

  const minutes = DateTime.fromISO(timesheet.endedAt).diff(DateTime.fromISO(timesheet.startedAt), 'minutes').minutes
  const lunchMinutes = Math.round((timesheet.lunch ?? 0) * 60)
  const totalMinutes = Math.round(minutes - lunchMinutes)

  return {
    totalMinutes,
    totalTime: minutesToDisplayTime(totalMinutes)
  }
}

export const isSameDate = (date1: string | null | undefined, date2: string | null | undefined, timeZone: string): boolean => {
  if (!date1 || !date2) return false

  const d1 = dayjs.tz(date1, timeZone).startOf('day')
  const d2 = dayjs.tz(date2, timeZone).startOf('day')
  return d1.isSame(d2)
}

export const enrichTimesheet = (timesheet: Timesheet, original: Timesheet | null, crew: EnrichedCrewMember[], isRemoved: boolean, visitDate: string, timeZone: string): EnrichedTimesheet => {
  const crewMember = crew.find(x => x.id === timesheet.crewMemberId)
  const { totalTime, totalMinutes } = calculateTotalTime(timesheet)

  return {
    ...timesheet,
    totalTime,
    totalMinutes,
    crewMemberName: crewMember?.name || `UNKNOWN [${timesheet.crewMemberId}]`,
    crewMemberAvatar: crewMember?.avatar || '',
    crewMemberInitials: crewMember?.initials || '',
    isRemoved,
    workareaIdChanged: original !== null && timesheet.workareaId !== original.workareaId,
    startedAtChanged: original !== null && timesheet.startedAt !== original.startedAt,
    endedAtChanged: original !== null && timesheet.endedAt !== original.endedAt,
    lunchChanged: original !== null && timesheet.lunch !== original.lunch,
    hasTimeRangeError: totalMinutes < 0 || (timesheet.endedAt !== undefined && totalMinutes === 0),
    hasOverlapError: false,
    cannotBeSubmitted: false,
    startedAtDateMismatch: !isSameDate(timesheet.startedAt, visitDate, timeZone),
    endedAtDateMismatch: timesheet.endedAt ? !isSameDate(timesheet.endedAt, visitDate, timeZone) : false
  }
}

export const enrichTimesheets = (state: LaborChanges, visit: Visit, crew: CrewMember[], timeZone: string, stateVisitDate?: string): EnrichedTimesheet[] => {
  const enrichedCrew = enrichCrewMembers(state, crew)
  const visitDate = stateVisitDate ?? visit.visitDate
  return [
    ...visit.timesheets.map(x => enrichTimesheet(state.changedLabor.find(y => y.id === x.id) ?? x, x, enrichedCrew, state.removedLaborIds.some(y => y === x.id), visitDate, timeZone)),
    ...state.addedLabor.map(x => enrichTimesheet(x, null, enrichedCrew, false, visitDate, timeZone))
  ]
}

export type EnrichedCrewMember = CrewMember & {
  initials: string
  selected: boolean
}

export const enrichCrewMembers = (state: LaborChanges, crew: CrewMember[] | undefined): EnrichedCrewMember[] => {
  if (!Array.isArray(crew)) {
    return []
  }

  return crew.map(x => {
    const selected = state.bulkLabor.selectedCrewMemberIds.some(y => y === x.id)
    const initials = x.name.split(/\s+/)
      .filter(x => x.length > 0)
      .map(x => x.charAt(0).toUpperCase())
      .filter((_, i) => i < 2)
      .join('')
    return { ...x, initials, selected }
  })
}

export type CrewMemberSummary = {
  id: string
  name: string
  summaryTime: string
  cannotBeSubmitted: boolean
}

export type LaborViewItem = EnrichedTimesheet | CrewMemberSummary

export const isEnrichedTimesheet = (item: LaborViewItem): item is EnrichedTimesheet => {
  return (item as EnrichedTimesheet).crewMemberId !== undefined
}

export const buildLaborView = (state: LaborChanges, visit: Visit, crew: CrewMember[], timeZone: string, stateVisitDate?: string): LaborViewItem[] => {
  const timesheets = enrichTimesheets(state, visit, crew, timeZone, stateVisitDate)
  const grouped = timesheets.reduce((all: { crewMemberId: number, timesheets: EnrichedTimesheet[] }[], x) => {
    let entry = all.find(y => y.crewMemberId === x.crewMemberId)
    if (!entry) {
      entry = { crewMemberId: x.crewMemberId, timesheets: [] }
      all.push(entry)
    }
    entry.timesheets.push(x)
    return all
  }, [])
  grouped.sort((a, b) => {
    return a.timesheets[0].crewMemberName.localeCompare(b.timesheets[0].crewMemberName)
  })
  return grouped.flatMap(x => {
    const summaryTimeMinutes = sum(x.timesheets, x => x.totalMinutes)
    const overlappingTimesheetIds = getOverlappingTimesheetIds(x.timesheets.filter(x => !x.isRemoved))
    const summary = {
      id: uniqueId(),
      name: x.timesheets[0].crewMemberName,
      summaryTime: minutesToDisplayTime(summaryTimeMinutes),
      cannotBeSubmitted: false
    }
    return [
      ...x.timesheets.map(t => ({
        ...t,
        hasOverlapError: overlappingTimesheetIds.includes(t.id),
        cannotBeSubmitted: overlappingTimesheetIds.includes(t.id) || t.hasTimeRangeError
      })),
      summary
    ]
  })
}

export const getOverlappingTimesheetIds = (timesheets: Timesheet[]): string[] => {
  const overlappingTimesheeets: string[] = []
  timesheets.forEach(a => {
    timesheets.forEach(b => {
      if (a.id === b.id) {
        return
      }

      const aStart = DateTime.fromISO(a.startedAt)
      const aEnd = a.endedAt ? DateTime.fromISO(a.endedAt) : aStart.plus({ years: 1 })

      const bStart = DateTime.fromISO(b.startedAt)
      const bEnd = b.endedAt ? DateTime.fromISO(b.endedAt) : bStart.plus({ years: 1 })

      if (DateTime.max(aStart, bStart) < DateTime.min(aEnd, bEnd)) {
        overlappingTimesheeets.push(a.id)
        return
      }
    })
  })
  return overlappingTimesheeets
}

export const getIdsOfWorkareasWhichHaveTimesheets = (visit: Visit, state: LaborChanges): number[] => {
  const timesheetWorkareas = Object.fromEntries(visit.timesheets.filter(x => x.workareaId).map(x => [x.id, x.workareaId!]))
  state.changedLabor.filter(x => x.workareaId).forEach(x => timesheetWorkareas[x.id] = x.workareaId!)
  state.addedLabor.filter(x => x.workareaId).forEach(x => timesheetWorkareas[x.id] = x.workareaId!)
  return Array.from(new Set(Object.values(timesheetWorkareas)))
}

export const clearWorkareaIds = (state: LaborChanges): LaborChanges => {
  return {
    ...state,
    addedLabor: state.addedLabor.map(x => ({ ...x, workareaId: undefined })),
    bulkLabor: {
      ...state.bulkLabor,
      workareaId: undefined
    }
  }
}

export const calculateDeltaDays = (a: string, b: string): number => {
  return DateTime.fromISO(b).diff(DateTime.fromISO(a)).as('days')
}

export const adjustTimesheetDay = (timesheet: Timesheet, deltaDays: number): Timesheet => {
  return {
    ...timesheet,
    startedAt: DateTime.fromISO(timesheet.startedAt).plus({ days: deltaDays }).toUTC().toISO(),
    endedAt: timesheet.endedAt && DateTime.fromISO(timesheet.endedAt).plus({ days: deltaDays }).toUTC().toISO()

  }
}
